<template>
  <div class="tld-tab">
    <div class='tld-tab__headers'>
      <div class="tld-tab__headers__search">
        <filtered-search
          @searchInput="searchChange"
          @filtersInput="filtersChange"
          :filter-items='filterItems'
          :show-filters="true"
          :filters="filters"
        />
      </div>
      <div class="tld-tab__headers__toggles">
        <v-switch
          v-model="showOnlyValidated"
          :label="$t('configuration.only.validated')"
          @change='onlyValidatedChanged'
        ></v-switch>
      </div>
      <div class="tld-tab__headers__toggles">
        <v-switch
          v-model="showHas360"
          :label="$t('configuration.has.360')"
          @change='has360Changed'
        ></v-switch>
      </div>
      <div class="tld-tab__headers__toggles">
        <v-switch
          v-model="showOnlyRepetitive"
          :label="$t('configuration.only.repetitive')"
          @change='onlyRepetitiveChanged'
        ></v-switch>
      </div>
    </div>
    <configuration-item-header
      tab="domains"
      :test-options="testItems"
      :url-count="urlCount"
      :selected-items="selectedItems"
      :excluded-items="excludedItems"
      :allSelected="isAllSelected"
      :all-items-count="domainsCount"
      :selectAllLabel="$t('configuration.selected.domains')"
      @clearSelected="resetSelectedItems"
      @select-all="selectAll"
      @apply='apply'
    >
      <template slot="additional-buttons">
        <v-btn class="add-tags" :disabled="addTagsDisabled" @click="openTagsPopup">
          {{ $t('configuration.add.tags') }}
        </v-btn>
        <download-c-s-v class="download-csv-btn" :class='{"disabled": downloadReportDisabled || dataLoading || topLevelDomainsLoading}' @click="downloadCSVReport"/>
      </template>
    </configuration-item-header>
    <div class='tld-content' v-if='!dataLoading && !topLevelDomainsLoading'>
      <div class="tab__body" v-if='listOfDomainsFiltered.length'>
        <div class='counter'> {{getCounter}} </div>
        <assets-column-headers
          :display='true'
          :headers='headersForTab'
          tab='topLevelDomain'
          :clickable='true'
          :sortDirection='getSortDirection'
          :sortOption='getSortOption'
          :clickedHeaderValue="clickedHeaderValue"
          @headerClicked='headerClicked'
        />
        <configuration-item-row
          v-for="(domain, index) in listOfDomainsFiltered"
          :website="domain"
          :show-open-link="true"
          tab="domains"
          :key="index"
          :is-main-tld='domain.isMainDomain || false'
          :is-supply-chain-org='isActiveOrgSupplyChain'
          :risk-score-value='getRiskScoreValue(domain.configData)'
          :testTypes="testTypesFormatted"
          :is-selected="isSelected(domain)"
          :amountOfTestCardInRow='amountOfTestCardInRow'
          :actionsWidth='actionsWidth'
          :warningTooltip="$t('configuration.vulnerable.asset.tooltip')"
          :lastScannedDate='lastScannedDate(domain.configData)'
          :assetsAmount="domain.assets"
          @openRiskScorePopup='openRiskScorePopup'
          @add-test="addTest"
          @update-sensitivity='updateSensitivity'
          @openSensitivityPopup='openSensitivityPopup'
          @openValidatePopup='openValidatePopup'
          @open360DegreesPopup='open360DegreesPopup'
          @checked="checkedValue"
          @openTagsPopup='openTagsPopup'
          @submitTagsInformation='submitTagsInformation'
          @openDeleteTldPopup='openDeleteTldPopup'
          @addTagFilter='addTagFilter'
          @openScanSettingsPopup='openScanSettingsPopup'
        />
      </div>
      <no-data
        v-else
        :message='$t("top.level.domain.no.data.message").toString()'
      >
        <div slot="action" v-if='IsTenantAdmin'>
          <v-btn class="configure-organizations-btn" @click="goToOrganizations">
            {{ $t('configure.organization.button.text') }}
          </v-btn>
        </div>
      </no-data>
      <div class="tld-tab__footer">
        <advanced-pagination
          tab='topLevelDomain'
          :pages-amount='pages'
          :page="page"
          :is-show-pagination='isShowPagination'
          @changedSelectedPage='changedSelectedPage'
          @changedItemsAmountPerPage='changedItemsAmountPerPage'
        />
      </div>
    </div>
    <div class='tld-tab-loading' v-else />
    <page-loading-overlay
      :show="topLevelDomainsLoading && currentTab === 'topLevelDomain'"
      :progress="topLevelDomainsProgress"
      contained
    />
    <validate-domain-popup
      :visible-popup='showValidatePopup'
      :website='tldToValidate'
      :validationFailed='validationFailed'
      @close='showValidatePopup = false'
      @validate='validateTld'
    />
    <sensitivity-selection-popup
      v-if='sensitivityPopup'
      :visible-popup='sensitivityPopup'
      :sensitivityIndex='getSensitivityIndex()'
      :loading='loadingForSensitivityPopup'
      @close='closeSensitivityPopup'
      @updateSensitivity='updateSensitivity'
    />
    <domain-scan-settings-popup
      v-if='domainScanSettingsPopup'
      :visible-popup='domainScanSettingsPopup'
      :loading='loadingForDomainScanSettingsPopup'
      :tld='domainScanSettingsPopupData.tld'
      :initial-custom-headers='domainScanSettingsPopupData.scanSettings.customHeaders'
      :initial-subdomain-filters='domainScanSettingsPopupData.scanSettings.subdomainFilters'
      :website='domainScanSettingsPopupData.website'
      @close='closeDomainScanSettingsPopup'
      @updateDomainSettings='updateDomainScanSettings'
    />
    <risk-score-popup
      :visible='riskScorePopup'
      :value='riskScoreFixedValue'
      :score='riskScoreColorAndLetter'
      :domains='[tldToRiskScore.configData]'
      :detailsSeries='detailsSeries'
      :labels='riskScoreLabels'
      :missing-data='missingDataRiskScore'
      :recalculate-required='riskScoreRecalculateRequired'
      :optimized='riskScoreOptimizedIndication'
      @recalculate='recalculateRiskScore'
      @closed='riskScorePopupClosed'
    />
    <three-hundred-sixty-degrees-popup
      v-if='tldToOverview && tldToOverview.configData'
      :visible-popup = 'show360DegreesPopup'
      :website = 'tldToOverview.configData'
      :subdomainTotalStats="subdomainTotalStats(tldToOverview)"
      @close = 'close360DegreesPopup'
    />
    <delete-domain-popup
      :visible-popup='showDeleteTldPopup'
      :tld='tldToDelete'
      @deleteTld='deleteTld'
      @closed='onDeleteTldPopupClosedButton'
    />
    <error-message :errorText="errorMessage" @close="errorMessage = ''" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  ADD_NEW_TEST_PROJECT,
  DELETE_CONFIGURATION,
  INITIATE_TEST_CARD,
  LOAD_TEST_CARDS_DOMAINS,
} from '@/store/project/actionTypes';
import ConfigurationItemRow from '@/components/configuration/ConfigurationItemRow.vue';
import ConfigurationItemHeader from '@/components/configuration/ConfigurationItemHeader.vue';
import { CONFIGURATION, CONFIGURATION_ACTION_TYPES, TOP_LEVEL_DOMAINS_HEADERS } from '@/constants/configuration';
import { TEST_TYPES_TLDS } from '@/constants/projects';
// eslint-disable-next-line no-unused-vars
import { UPDATE_SENSITIVITY, UPDATE_DOMAIN_SCAN_SETTINGS, VALIDATE_TLD } from '@/store/user/actionTypes';
import AssetsColumnHeaders from '@/components/configuration/AssetsColumnHeaders.vue';
import PageLoadingOverlay from '@/components/common/PageLoadingOverlay.vue';
import FilteredSearch from '@/components/common/FilteredSearch.vue';
import ValidateDomainPopup from '@/components/popup/ValidateDomainPopup.vue';
import ThreeHundredSixtyDegreesPopup from '@/components/popup/ThreeHundredSixtyDegreesPopup.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import SensitivitySelectionPopup from '@/components/popup/SensitivtySelectionPopup.vue';
import RiskScorePopup from '@/components/popup/RiskScorePopup.vue';
import {
  LOAD_PASSIVE_RISK_SCORE,
  RECALCULATE_RISK_SCORE,
  RESET_PASSIVE_RISK_SCORE,
} from '@/store/statistics/actionTypes';
import AdvancedPagination from '@/components/common/AdvancedPagination.vue';
import { UPDATE_ITEMS_PER_PAGE } from '@/store/pagination/actionTypes';
import DownloadCSV from '@/assets/icons/download_csv.svg?inline';
import moment from 'moment';
import DeleteDomainPopup from '@/components/popup/DeleteDomainPopup.vue';
import { getGroupedTimezonesList } from '@/utils/dateUtils';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import NoData from '@/components/common/NoData.vue';
import { views } from '@/router/constants';
import store from '@/store';
import {
  CHANGE_TOP_LEVEL_DOMAIN_LIST_OPTIONS,
  UPDATE_SELECTED_TOP_LEVEL_DOMAINS,
  CLEAR_TOP_LEVEL_DOMAINS_CACHE,
} from '@/store/configuration/actionTypes';
import {downloadCSVReportByTab} from '@/services/repository';
import {SET_DOMAIN_FILTER} from '@/store/filters/actionTypes';
import riskScoreLetters from '@/constants/riskScore';
import DomainScanSettingsPopup from '@/components/popup/DomainScanSettingsPopup.vue';

export default {
  name: 'TopLevelDomainsTab',
  mixins: [disableOuterScrollOnPopupVisible],
  components: {
    DomainScanSettingsPopup,
    NoData,
    DeleteDomainPopup,
    AdvancedPagination,
    RiskScorePopup,
    SensitivitySelectionPopup,
    ErrorMessage,
    ValidateDomainPopup,
    ThreeHundredSixtyDegreesPopup,
    AssetsColumnHeaders,
    ConfigurationItemHeader,
    ConfigurationItemRow,
    PageLoadingOverlay,
    FilteredSearch,
    DownloadCSV,
  },
  props: {
    currentTab: {
      required: false,
    },
    actionsWidth: {
      type: Number,
      required: false,
      default: 1120,
    },
    amountOfTestCardInRow: {
      type: Number,
      required: false,
      default: 5,
    },
    forceLoadTopLevelDomain: {
      type: Boolean,
      required: false,
      default: false,
    },
    forceResetSelectedItemsTopLevelDomain: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      errorMessage: '',
      headersForTab: [
        'tld.tab.main.asset.header',
        'tld.tab.risk.score.header',
        'tld.tab.validated.header',
        'tld.tab.sensitivity.header',
        'tld.tab.waf.header',
        'tld.tab.csp.header',
        'tld.tab.assets.amount.header',
        'tld.tab.subdomain.stats.header',
        'tld.tab.tech.stats.header',
        'tld.tab.cloud.stats.header',
        'last.scanned.tld',
      ],
      riskScorePopup: false,
      sensitivityPopup: false,
      domainScanSettingsPopup: false,
      validationFailed: false,
      showValidatePopup: false,
      showDeleteTldPopup: false,
      tldToDelete: '',
      tldToValidate: {},
      tldToUpdateSensitivity: {},
      tldToRiskScore: {},
      tldToOverview: {},
      show360DegreesPopup: false,
      riskScoreColorAndLetter: {},
      detailsPassiveRiskScore: {},
      missingDataRiskScore: {},
      riskScoreFixedValue: 0,
      riskScoreLabels: [],
      detailsSeries: [],
      riskScoreRecalculateRequired: false,
      riskScoreOptimizedIndication: false,
      value: false,
      indeterminate: false,
      showOnlyValidated: false,
      showHas360: false,
      showOnlyRepetitive: false,
      sortTLD: {
        [TOP_LEVEL_DOMAINS_HEADERS['tld.tab.main.asset.header']]: 'asc',
      },
      clickedHeaderValue: {},
      search: '',
      filterSearch: [],
      filterItems: [],
      filters: [
        {name: 'Tag', attr: 'tag'},
        {name: 'Risk Score', attr: 'riskScore'},
        {name: 'Sensitivity', attr: 'sensitivity'},
        {name: 'CSP', attr: 'CSP'},
        {name: 'Amount Of Assets', attr: 'assets'},
        {name: 'Last Scanned Date', attr: 'lastScannedDate'},
      ],
      page: 1,
      initialWatchers: true,
      loadingCounter: 0,
      selectedItems: [],
      excludedItems: [],
      isAllSelected: false,
      filtersForPayload: {
        search: '',
        filter: [],
        toggles: {
          onlyValidated: false,
          has360: false,
        },
      },
      firstTimeCalled: false,
      pollingTopLevelDomains: null,
      loadingForSensitivityPopup: false,
      loadingForDomainScanSettingsPopup: false,
      domainScanSettingsPopupData: {},
    };
  },
  async beforeDestroy() {
    clearInterval(this.pollingTopLevelDomains);
    await this.$store.dispatch(RESET_PASSIVE_RISK_SCORE);
    await this.$store.dispatch(CLEAR_TOP_LEVEL_DOMAINS_CACHE);
  },
  async created() {
    if (this.currentTab === 'topLevelDomain') {
      if (this.domainFilter.length > 0 || this.organizationFilter.length > 0) {
        await this.loadTopLevelDomains();
        await this.loadTestCardsForCurrentDomains();
      }
    }
  },
  watch: {
    sensitivityPopup() {
      this.disableOuterScrollWhenPopupOpen(this.sensitivityPopup);
    },
    show360DegreesPopup() {
      this.disableOuterScrollWhenPopupOpen(this.show360DegreesPopup);
    },
    async currentTab() {
      if (this.currentTab === 'topLevelDomain' && (!this.firstTimeCalled || this.forceLoadTopLevelDomain)) {
        this.firstTimeCalled = true;
        await this.loadTopLevelDomains();
        await this.loadTestCardsForCurrentDomains();
        await this.$emit('forceRerenderTopLevelDomainTab', false);
      }
      this.resetSelectedItems();
    },
    async domainFilter() {
      this.resetSelectedItems();
      if (this.currentTab !== 'topLevelDomain' && this.domainFilter.length) {
        this.firstTimeCalled = false;
      }
      if (this.currentTab === 'topLevelDomain') {
        if (this.loadingCounter > 0 || this.domainFilter.length !== 0) {
          this.page = 1;
          await this.loadTopLevelDomains();
          await this.loadTestCardsForCurrentDomains();
          this.loadingCounter = 0;
        }
      }
    },
    async organizationFilter() {
      if (this.currentTab === 'topLevelDomain') {
        if (!this.initialWatchers && this.domainFilter.length === 0 && this.loadingCounter === 0) {
          this.page = 1;
          await this.loadTopLevelDomains();
          await this.loadTestCardsForCurrentDomains();
        } else {
          this.loadingCounter += 1;
          this.initialWatchers = false;
        }
      }
    },
    async sortTLD() {
      this.page = 1;
      await this.loadTopLevelDomains();
      await this.loadTestCardsForCurrentDomains();
    },
    async showOnlyRepetitive() {
      await this.loadTestCardsForCurrentDomains();
    },
    excludedItems() {
      if (this.excludedItems.length === this.filtersForPayload.total && this.isAllSelected) {
        this.isAllSelected = false;
      }
    },
    selectedItems() {
      if (this.selectedItems.length === this.filtersForPayload.total && !this.isAllSelected) {
        this.isAllSelected = true;
      }
    },
    async isAllSelected() {
      if (!this.isAllSelected) {
        this.excludedItems = [];
        this.resetFiltersForPayload();
      }
      if (this.isAllSelected) {
        const items = [];
        this.topLevelDomains.forEach(item => {
          items.push({ ...item, selected: true });
        });
        await this.$store.dispatch(UPDATE_SELECTED_TOP_LEVEL_DOMAINS, items);
        this.selectedItems = [];
        this.resetFiltersForPayload();
      }
    },
    forceResetSelectedItemsTopLevelDomain() {
      if (this.forceResetSelectedItemsTopLevelDomain) {
        this.resetSelectedItems();
      }
    },
    async forceLoadTopLevelDomain() {
      if (this.forceLoadTopLevelDomain) {
        await this.loadTopLevelDomains(true);
      }
    },
  },
  computed: {
    ...mapGetters([
      'downloadReportDisabled',
      'dataLoading',
      'topLevelDomainsLoading',
      'domains',
      'passiveRiskScore',
      'paginationItemsPerPageByTabs',
      'domainFilter',
      'topLevelDomainsPaginationState',
      'topLevelDomains',
      'organizationFilter',
      'organizations',
      'domainsPerOrganization',
      'topLevelDomainsRefId',
      'tldRunningInterval',
      'topLevelDomainsProgress',
    ]),
    riskScoreLetters() {
      return riskScoreLetters;
    },
    isActiveOrgSupplyChain() {
      if (!this.organizationFilter?.length) return false;
      const activeOrg = this.organizations?.find(o => o.name === this.organizationFilter[0]);
      return !!activeOrg?.isSupplyChain;
    },
    IsTenantAdmin: () => store.getters.permissions?.includes('all.tenant.users') || false,
    timezonesItems() {
      return getGroupedTimezonesList().map(tz => ({
        value: tz.offset,
        name: tz.name,
        id: tz.id,
      }));
    },
    sensitivityOptions() {
      return Object.values(CONFIGURATION.SensitivityOptions);
    },
    listOfDomainsFiltered() {
      return this.topLevelDomains;
    },
    domainsCount() {
      return parseInt(this.topLevelDomainsPaginationState.total, 10);
    },
    urlCount() {
      if (this.isAllSelected) {
        return (this.filtersForPayload.total - this.excludedItems.length + this.selectedItems.length);
      }
      return this.selectedItems.length || 0;
    },
    testTypesFormatted() {
      return Object.values(TEST_TYPES_TLDS);
    },
    testItems() {
      return this.testTypesFormatted.map(test => ({ value: test, title: this.$t(`test.testType.${test}`) }));
    },
    pages() {
      return this.topLevelDomainsPaginationState?.pageCount || Math.ceil(this.topLevelDomains.length / this.itemsPerPage);
    },
    isShowPagination() {
      return this.pages > 1;
    },
    itemsPerPage() {
      return this.paginationItemsPerPageByTabs.topLevelDomain;
    },
    addTagsDisabled() {
      if (!this.isAllSelected) {
        return !this.selectedItems.length;
      }
      return false;
    },
    getCounter() {
      let from = ((this.page - 1) * this.itemsPerPage) + 1;
      let to = this.page * this.itemsPerPage;
      if (Number(this.page) === this.pages) {
        to = this.domainsCount;
      }
      if (this.domainsCount < this.itemsPerPage) {
        from = 1;
        to = this.domainsCount;
      }
      if (to === 0) {
        from = 0;
      }
      return `${from} to ${to} of ${this.domainsCount}`;
    },
    getSortDirection() {
      return Object.values(this.sortTLD)?.includes('desc') ? 'keyboard_arrow_down' : 'keyboard_arrow_up';
    },
    getSortOption() {
      return this.headersForTab.filter(header => Object.keys(this.sortTLD)[0] === TOP_LEVEL_DOMAINS_HEADERS[header])[0] || '';
    },
  },
  methods: {
    getIndexOfColorByRiskScoreValue(score) {
      if (score >= 95) {
        return 0;
      }
      if (score >= 81) {
        return 1;
      }
      if (score >= 71) {
        return 2;
      }
      if (score >= 61) {
        return 3;
      }
      if (score >= 51) {
        return 4;
      }
      return 5;
    },
    getScoreAndColorForRiskScore(score) {
      const index = this.getIndexOfColorByRiskScoreValue(score);
      const result = this.riskScoreLetters[index].letter;
      const {color} = this.riskScoreLetters[index];
      return {
        text: result,
        color,
      };
    },
    async recalculateRiskScore(domains) {
      const tldToRecalculate = {...this.tldToRiskScore};
      await this.$store.dispatch(RECALCULATE_RISK_SCORE, domains);
      const updatedDomainIndex = this.topLevelDomains.findIndex(tldData => tldData.uuid === tldToRecalculate.uuid);
      if (updatedDomainIndex > -1) {
        // UPDATE TLD DATA
        this.topLevelDomains[updatedDomainIndex].riskScore = this.passiveRiskScore[tldToRecalculate.configData].score;
        this.topLevelDomains[updatedDomainIndex].recalculateRequired = false;
        // UPDATE RISK SCORE POPUP DATA
        if (this.tldToRiskScore.configData === this.topLevelDomains[updatedDomainIndex].configData) {
          this.riskScoreColorAndLetter = this.getScoreAndColorForRiskScore(this.passiveRiskScore[tldToRecalculate.configData].score);
          this.riskScoreFixedValue = this.getRiskScoreValue(tldToRecalculate.configData);
          this.detailsPassiveRiskScore = this.formatPassiveRiskScoreDetails(this.passiveRiskScore[tldToRecalculate.configData].details);
          this.missingDataRiskScore = this.getDataMissingRiskScore();
          this.riskScoreLabels = Object.keys(this.detailsPassiveRiskScore);
          this.detailsSeries = Object.values(this.detailsPassiveRiskScore);
          this.riskScoreRecalculateRequired = this.passiveRiskScore[tldToRecalculate.configData].recalculateRequired;
          this.riskScoreOptimizedIndication = this.passiveRiskScore[tldToRecalculate.configData].optimized;
        }
      }
    },
    async riskScorePopupClosed() {
      this.riskScorePopup = false;
    },
    async loadTopLevelDomains(loadEmpty = false) {
      if (this.currentTab === 'topLevelDomain' && (this.domainFilter.length || loadEmpty)) {
        clearInterval(this.pollingTopLevelDomains);
        await this.$store.dispatch(CHANGE_TOP_LEVEL_DOMAIN_LIST_OPTIONS, {
          sort: this.sortTLD,
          limit: this.itemsPerPage,
          page: this.page,
          search: this.search,
          filter: this.filterSearch,
          onlyValidated: this.showOnlyValidated,
          has360: this.showHas360,
          domain: this.domainFilter || [],
          organizations: this.organizationFilter || [],
          turnOnLoading: true,
          tab: 'topLevelDomain',
          selectAllFilters: {
            ...this.filtersForPayload, selected: this.selectedItems, excluded: this.excludedItems, isAllSelected: this.isAllSelected,
          },
        });
        if (this.topLevelDomainsRefId && this.topLevelDomainsRefId !== '') {
          this.pollingTopLevelDomains = setInterval(async () => {
            if (!this.topLevelDomainsRefId || this.topLevelDomainsRefId === '') {
              clearInterval(this.pollingTopLevelDomains);
            } else if (!this.tldRunningInterval) {
              await this.$store.dispatch(CHANGE_TOP_LEVEL_DOMAIN_LIST_OPTIONS, {
                sort: this.sortTLD,
                limit: this.itemsPerPage,
                page: this.page,
                search: this.search,
                filter: this.filterSearch,
                onlyValidated: this.showOnlyValidated,
                has360: this.showHas360,
                domain: this.domainFilter || [],
                organizations: this.organizationFilter || [],
                turnOnLoading: true,
                tab: 'topLevelDomain',
                refId: this.topLevelDomainsRefId,
                selectAllFilters: {
                  ...this.filtersForPayload, selected: this.selectedItems, excluded: this.excludedItems, isAllSelected: this.isAllSelected,
                },
              });
            }
          }, 3000);
        }
      }
    },
    resetFiltersForPayload() {
      this.filtersForPayload = {
        search: this.search,
        filter: this.filterSearch,
        toggles: {
          onlyValidated: this.showOnlyValidated,
          has360: this.showHas360,
        },
        total: this.topLevelDomainsPaginationState.total,
      };
    },
    resetSelectedItems() {
      this.selectedItems = [];
      this.excludedItems = [];
      this.isAllSelected = false;
      this.filtersForPayload = {
        search: '',
        filter: [],
        toggles: {
          onlyValidated: false,
          has360: false,
        },
      };
    },
    async loadTestCardsForCurrentDomains() {
      await this.$store.dispatch(LOAD_TEST_CARDS_DOMAINS, {
        onlyRepetitive: this.showOnlyRepetitive,
        domain: [...this.topLevelDomains.map(item => item.configData)],
      });
    },
    async goToOrganizations() {
      await this.$router.push({ name: views.SETTINGS, hash: '#organizations-settings' });
    },
    getSensitivityIndex() {
      const index = this.sensitivityOptions.findIndex(item => item === this.tldToUpdateSensitivity.sensitivity);
      // In case there is no sensitivity for domain, we will take HIGH as default
      return index < 0 ? 2 : index;
    },
    closeSensitivityPopup() {
      this.loadingForSensitivityPopup = false;
      this.sensitivityPopup = false;
    },
    closeDomainScanSettingsPopup() {
      this.loadingForDomainScanSettingsPopup = false;
      this.domainScanSettingsPopup = false;
      this.domainScanSettingsPopupData = {};
    },
    async onDeleteTldPopupClosedButton() {
      this.showDeleteTldPopup = false;
      this.tldToDelete = '';
    },
    openDeleteTldPopup(tld) {
      this.showDeleteTldPopup = true;
      this.tldToDelete = tld;
    },
    openScanSettingsPopup(payload) {
      this.domainScanSettingsPopupData = payload;
      this.domainScanSettingsPopup = true;
    },
    async deleteTld() {
      try {
        const updatedDomains = this.domainFilter.filter(domain => domain !== this.tldToDelete);
        if (updatedDomains.length === 0) {
          const orgDomains = Array.from(this.domainsPerOrganization.entries()).filter(item => item[0] === this.organizationFilter[0]);
          updatedDomains.push(orgDomains[0][1].find(domain => domain !== this.tldToDelete));
        }
        await this.$store.dispatch(DELETE_CONFIGURATION, {
          payload: [({ data: this.tldToDelete, metaData: [], tld: this.tldToDelete })],
          configType: CONFIGURATION.CONFIG_TYPES.DOMAIN,
          successMessageType: CONFIGURATION_ACTION_TYPES.DELETE_DOMAIN,
        });
        await this.$store.dispatch(SET_DOMAIN_FILTER, updatedDomains);
        await this.loadTopLevelDomains(true);
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    lastScannedDate(domain) {
      const tldObject = this.topLevelDomains.filter(item => item.configData === domain)[0];
      if (tldObject.lastScannedDate) {
        return moment(tldObject.lastScannedDate).format('DD MMM YYYY');
      }
      return '';
    },
    subdomainTotalStats(tldToOverview) {
      return tldToOverview?.statistics?.data?.subdomains?.total || 0;
    },
    async close360DegreesPopup(domainAddedFlag = false) {
      this.show360DegreesPopup = false;
      if (domainAddedFlag) {
        await this.loadTopLevelDomains();
      }
    },
    async downloadCSVReport() {
      if (!this.downloadReportDisabled) {
        const payload = {
          sort: this.sortTLD,
          limit: this.itemsPerPage,
          page: this.page,
          search: this.search,
          filter: this.filterSearch,
          toggles: {
            onlyValidated: this.showOnlyValidated,
            has360: this.showHas360,
          },
          domain: this.domainFilter || [],
          organizations: this.organizationFilter || [],
          tab: 'topLevelDomain',
        };
        const dataStream = await downloadCSVReportByTab(payload);
        const blob = new Blob([dataStream], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `${Date.now()}-top-level-domain.csv`);
        document.body.appendChild(link);
        link.click();
      }
    },
    async changedItemsAmountPerPage(itemsPerPage) {
      window.scrollTo(0, 0);
      this.page = 1;
      await this.$store.dispatch(UPDATE_ITEMS_PER_PAGE, { itemsPerPage, tab: 'topLevelDomain'});
      await this.loadTopLevelDomains();
      await this.loadTestCardsForCurrentDomains();
    },
    async changedSelectedPage(currentPage) {
      window.scrollTo(0, 0);
      this.page = currentPage;
      await this.loadTopLevelDomains();
      await this.loadTestCardsForCurrentDomains();
    },
    formatPassiveRiskScoreDetails(details) {
      const detailsRiskScoreObject = {
        hijack: 0,
        smtp: 0,
        dns: 0,
        cloud: 0,
        dataleaks: 0,
        employees: 0,
        waf: 0,
        tech: 0,
        ports: 0,
      };
      const riskScoreParameters = Object.keys(detailsRiskScoreObject);
      riskScoreParameters.forEach(param => {
        detailsRiskScoreObject[param] += details[param] || 0;
      });
      riskScoreParameters.forEach(param => {
        detailsRiskScoreObject[param] = parseFloat(parseFloat(detailsRiskScoreObject[param]).toFixed(1));
      });
      return detailsRiskScoreObject;
    },
    getDataMissingRiskScore() {
      return this.passiveRiskScore[this.tldToRiskScore.configData].dataMissing
        ? this.passiveRiskScore[this.tldToRiskScore.configData].dataMissing
        : {
          hijack: false,
          smtp: false,
          dns: false,
          cloud: false,
          dataleaks: false,
          employees: false,
          waf: false,
          tech: false,
          ports: false,
        };
    },
    async headerClicked(payload) {
      const sortDirection = payload.clicks % 2 === 0 ? 'asc' : 'desc';
      const sortBy = TOP_LEVEL_DOMAINS_HEADERS[payload.header];
      this.sortTLD = {
        [sortBy]: sortDirection,
      };
      this.clickedHeaderValue = {
        [payload.header]: payload.clicks,
      };
    },
    syncDomainRiskScore(domain, rawScore) {
      // The API may return the DB numeric as a string, same as the list does.
      const score = parseFloat(rawScore);
      if (Number.isNaN(score)) {
        return;
      }
      const row = this.topLevelDomains.find(tld => tld.tld === domain);
      if (row) {
        row.riskScore = score;
      }
      this.riskScoreColorAndLetter = this.getScoreAndColorForRiskScore(score);
      this.riskScoreFixedValue = parseFloat((100 - score).toFixed(1));
    },
    getRiskScoreValue(domain) {
      let score = 100;
      this.topLevelDomains.forEach(tld => {
        if (tld.tld === domain && tld.riskScore) {
          const fixedSum = (100 - tld.riskScore).toFixed(1);
          score = parseFloat(fixedSum);
        }
      });
      return score;
    },
    async openRiskScorePopup(payload) {
      this.tldToRiskScore = payload.website;
      this.riskScoreColorAndLetter = payload.score;
      this.riskScoreFixedValue = payload.fixedValue;
      await this.$store.dispatch(LOAD_PASSIVE_RISK_SCORE, { domain: [this.tldToRiskScore.configData] });
      this.detailsPassiveRiskScore = this.formatPassiveRiskScoreDetails(this.passiveRiskScore[this.tldToRiskScore.configData].details);
      this.valueRiskScore = this.passiveRiskScore[this.tldToRiskScore.configData].score;
      // The row's score was loaded with the list and can be older than the indicators just fetched
      // (e.g. after a suppression recalculated it). Show the fresh score and update the row too.
      this.syncDomainRiskScore(this.tldToRiskScore.configData, this.valueRiskScore);
      this.missingDataRiskScore = this.getDataMissingRiskScore();
      this.riskScoreLabels = Object.keys(this.detailsPassiveRiskScore);
      this.detailsSeries = Object.values(this.detailsPassiveRiskScore);
      this.riskScoreRecalculateRequired = this.passiveRiskScore[this.tldToRiskScore.configData].recalculateRequired;
      this.riskScoreOptimizedIndication = this.passiveRiskScore[this.tldToRiskScore.configData].optimized;
      this.riskScorePopup = true;
    },
    async openSensitivityPopup(website) {
      this.tldToUpdateSensitivity = website;
      this.loadingForSensitivityPopup = false;
      this.sensitivityPopup = true;
    },
    async validateTld() {
      await this.$store.dispatch(VALIDATE_TLD, { tld: this.tldToValidate.configData }).then(response => {
        this.validationFailed = response === false;
      });
    },
    async updateSensitivity(sensitivity) {
      this.loadingForSensitivityPopup = true;
      await this.$store.dispatch(UPDATE_SENSITIVITY, { tld: this.tldToUpdateSensitivity.configData, sensitivity });
      const updatedDomainIndex = this.topLevelDomains.findIndex(tldData => tldData.uuid === this.tldToUpdateSensitivity.uuid);
      if (updatedDomainIndex > -1) {
        this.topLevelDomains[updatedDomainIndex].sensitivity = sensitivity;
        try {
          // AFTER UPDATE OF SENSITIVITY, RISK SCORE SHOULD BE RECALCULATED
          await this.$store.dispatch(LOAD_PASSIVE_RISK_SCORE, { domain: [this.tldToUpdateSensitivity.configData] });
          const riskScoreForUpdatedDomain = this.formatPassiveRiskScoreDetails(this.passiveRiskScore[this.tldToUpdateSensitivity.configData].details);
          const riskScoreValuesForUpdatedDomain = Object.values(riskScoreForUpdatedDomain);
          if (!(riskScoreValuesForUpdatedDomain.length === 0 || riskScoreValuesForUpdatedDomain.every(item => item === 0))) {
            this.topLevelDomains[updatedDomainIndex].recalculateRequired = true;
          }
        } catch (e) {
          this.topLevelDomains[updatedDomainIndex].recalculateRequired = false;
        }
      }
      this.loadingForSensitivityPopup = false;
      this.sensitivityPopup = false;
    },
    async updateDomainScanSettings(settings) {
      this.loadingForDomainScanSettingsPopup = true;
      await this.$store.dispatch(UPDATE_DOMAIN_SCAN_SETTINGS, settings);
      const updatedDomainIndex = this.topLevelDomains.findIndex(tldData => tldData.uuid === settings.website.uuid);
      if (updatedDomainIndex > -1) {
        this.topLevelDomains[updatedDomainIndex].scanSettings = settings.scanSettings;
      }
      this.loadingForDomainScanSettingsPopup = false;
      this.domainScanSettingsPopup = false;
    },
    openValidatePopup(website) {
      this.tldToValidate = website;
      this.validationFailed = false;
      this.showValidatePopup = true;
    },
    open360DegreesPopup(website) {
      this.tldToOverview = website;
      this.show360DegreesPopup = true;
    },
    calculateTimeZone() {
      const defaultBrowserTimeZoneValue = String(moment().utcOffset());
      const defaultBrowserTimeZoneObj = this.timezonesItems.find(timeZoneObj => timeZoneObj.value === defaultBrowserTimeZoneValue);
      return defaultBrowserTimeZoneObj?.id || null;
    },
    checkedValue(item) {
      if (this.isAllSelected) {
        if (item?.selected !== undefined) {
          if (item.selected) {
            const index = this.excludedItems.findIndex(data => data === item.configData);
            if (index !== -1) {
              this.excludedItems.splice(index, 1);
            } else {
              this.excludedItems.push(item.configData);
            }
          } else {
            const index = this.selectedItems.findIndex(data => data === item.configData);
            if (index !== -1) {
              this.selectedItems.splice(index, 1);
            } else {
              this.selectedItems.push(item.configData);
            }
            if (this.excludedItems.includes(item.configData)) {
              const excludeIndex = this.excludedItems.findIndex(data => data === item.configData);
              this.excludedItems.splice(excludeIndex, 1);
              this.filtersForPayload.total -= 1;
            }
          }
        } else {
          const index = this.excludedItems.findIndex(data => data === item.configData);
          if (index !== -1) {
            this.excludedItems.splice(index, 1);
          } else {
            this.excludedItems.push(item.configData);
          }
        }
      } else {
        const index = this.selectedItems.findIndex(data => data === item.configData);
        if (index !== -1) {
          this.selectedItems.splice(index, 1);
        } else {
          this.selectedItems.push(item.configData);
        }
      }
    },
    async addTest({ test }) {
      const testWithTld = test;
      testWithTld.tld = test.url;
      try {
        const createdTest = await this.$store.dispatch(ADD_NEW_TEST_PROJECT, testWithTld);
        if (!testWithTld.schedule && createdTest) {
          try {
            await this.$store.dispatch(INITIATE_TEST_CARD, [createdTest.id]);
          } catch (e) {
            this.errorMessage = e.message;
          }
        }
      } catch (e) {
        this.errorMessage = e.message;
      }
      await this.loadTestCardsForCurrentDomains();
    },
    selectAll() {
      this.isAllSelected = !this.isAllSelected;
      if (this.selectedItems.length) {
        this.selectedItems = [];
      }
      if (this.excludedItems.length) {
        this.excludedItems = [];
      }
      this.resetFiltersForPayload();
    },
    isSelected(asset) {
      return this.selectedItems.includes(asset.configData) ||
        (this.isAllSelected && asset?.selected !== undefined && asset.selected && !this.excludedItems.includes(asset.configData)) ||
        (this.isAllSelected && asset?.selected === undefined && !this.excludedItems.includes(asset.configData));
    },
    openTagsPopup(asset = []) {
      let selected = this.selectedItems;
      if (asset.length) {
        selected = asset;
      }
      const payload = {
        selected,
        excluded: this.excludedItems,
        isAllSelected: this.isAllSelected,
        search: this.filtersForPayload?.search || '',
        filter: Object.keys(this?.filtersForPayload)?.length ? JSON.stringify(Object.fromEntries(this.filtersForPayload.filter)) : {},
        toggles: {
          onlyValidated: this.filtersForPayload?.toggles?.onlyValidated || false,
          has360: this.filtersForPayload?.toggles?.has360 || false,
        },
        domain: this.domainFilter || [],
        organizations: this.organizationFilter || [],
        tab: 'topLevelDomain',
      };
      this.$emit('toggleTagsPopup', payload);
    },
    async submitTagsInformation(payload) {
      await this.$emit('submitTagsInformation', { items: payload.items, configType: payload.configType, action: payload.action });
    },
    async filtersChange(e) {
      this.filterSearch = e;
      this.filterSearch = this.extractFilters();
      this.page = 1;
      await this.loadTopLevelDomains();
      await this.loadTestCardsForCurrentDomains();
    },
    async searchChange(e) {
      this.search = e.trim();
      this.search = this.extractSearchString();
      this.page = 1;
      await this.loadTopLevelDomains();
      await this.loadTestCardsForCurrentDomains();
    },
    extractSearchString() {
      if (!this.search) {
        return '';
      }
      let results = this.search.split(' ');
      results = results.filter(result => !result.includes(':'));
      return results.join(' ').trim();
    },
    addTagFilter(tagKey) {
      this.filterItems = [`tag: ${tagKey}`];
    },
    extractFilters() {
      const filtersMap = new Map();
      if (this.search) {
        let results = this.search.split(' ');
        results = results.filter(result => result.includes(':'));
        results.forEach(item => {
          const filter = item.split(':');
          if (filter.length > 1 && filter[1].length > 0) {
            filtersMap.set(filter[0], filter[1].split(',').map(element => element.trim()));
          }
        });
      }
      if (this.filterSearch.length > 0) {
        this.filterSearch.forEach(item => {
          const filter = item.split(':');
          if (filtersMap.has(filter[0])) {
            const filtersValues = filtersMap.get(filter[0]);
            filter[1].split(',').forEach(value => {
              filtersValues.push(value.trim());
            });
            filtersMap.set(filter[0], filtersValues);
          } else {
            const filtersValues = [];
            filter[1].split(',').forEach(value => {
              filtersValues.push(value.trim());
            });
            filtersMap.set(filter[0], filtersValues);
          }
        });
      }
      return filtersMap;
    },
    async apply() {
      await this.$emit('apply');
    },
    async onlyValidatedChanged() {
      this.page = 1;
      await this.loadTopLevelDomains();
      await this.loadTestCardsForCurrentDomains();
    },
    async onlyRepetitiveChanged() {
      this.page = 1;
      await this.loadTopLevelDomains();
      await this.loadTestCardsForCurrentDomains();
    },
    async has360Changed() {
      this.page = 1;
      await this.loadTopLevelDomains();
      await this.loadTestCardsForCurrentDomains();
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.configure-organizations-btn.configure-organizations-btn {
  width: fit-content;
  padding: $space-1 $space-1;
  background-color: var(--brand-primary);
  border-radius: $radius-sm;
  box-shadow: $shadow-sm;
  color: var(--text-inverse);
  font-family: $default-application-font-medium;
  font-size: $font-size-sm;
  font-weight: 500;
  letter-spacing: 1.1px;
  line-height: $font-size-md;
  text-align: center;
  margin-bottom: $space-2;
}

.tld-tab-loading {
  align-self: center;
  justify-self: center;
  margin-top: 20%;
}

.tld-tab {
  display: flex;
  flex-direction: column;
  padding: $space-4 $space-8;
  position: relative; // Required for contained loading overlay
  min-height: calc(100vh - 200px); // Ensure overlay has space to display
  background-color: var(--bg-primary);

  .counter {
    font-family: $default-application-font-regular;
    font-size: $font-size-sm;
    text-transform: uppercase;
    color: var(--text-secondary);
  }

  &__headers {
    display: flex;
    margin-top: rem(-19);
    margin-left: rem(-1);

    &__search {
      width: rem(700);
    }

    &__filters_row {
      margin-top: rem(-20);
      margin-bottom: $space-4;
      max-width: rem(500);
    }

    &__toggles {
      padding-left: $space-12;
      margin-top: $space-2;
    }
  }

  &__footer {
    margin-top: $space-6;
  }

  .add-tags.add-tags {
    margin-left: $space-5;
    background-color: var(--text-secondary) !important;
    border: none !important;
    border-radius: $radius-sm;
    box-shadow: $shadow-sm;
    color: var(--text-inverse) !important;
    font-family: $default-application-font-medium;
    font-size: $font-size-sm;
    height: $btn-height-md;
    letter-spacing: 1.1px;
    line-height: $font-size-md;
    width: rem(100);
    text-transform: uppercase !important;
  }

  .download-csv-btn {
    color: var(--text-inverse);
    font-family: $default-application-font-medium;
    font-size: $font-size-sm;
    height: $btn-height-md;
    letter-spacing: 1.1px;
    line-height: $font-size-md;
    width: rem(78);

    &:hover {
      cursor: pointer;
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
